// L1 (boot-health #3986): bound the cold-boot wait on the progress-bar animation.
//
// The cold-boot "finishing" step used to `await this.animateProgressBar(1)`, which
// awaited the raw `Animation.finished` promise of a 1 s width animation, with no
// timeout and outside any try/catch. Two ways that wedged boot:
//   1. A PAUSED animation never settles. The browser pauses animations on a hidden
//      tab or a minimized PWA, so `finished` never resolves and boot hangs forever
//      at `cold:finishing` (11% of splash hangs, still live on v1.28.2 with every
//      database already ready).
//   2. A CANCELLED animation REJECTS `finished`. Because the await sat outside a
//      try/catch, that rejection surfaced as an unhandled rejection and the splash
//      stayed up forever.
//
// The fix keeps the visual animation but stops *depending* on it to unblock boot:
// wait for the animation to finish OR a short delay, whichever comes first, and
// never let a rejection escape. The logic lives here as a pure, node-loadable module
// (same pattern as splash-watchdog.ts) so it can be unit-tested; app.component.ts
// delegates to it (pinned by the source tripwires in the test).

// How long the cold-boot "finishing" step is willing to wait for the progress-bar
// animation to settle before it stops waiting and lets boot continue. The animation
// itself runs for 1000 ms (see animateProgressBar in app.component.ts); the extra
// 500 ms of headroom covers normal scheduling jitter on a healthy boot so the visual
// fill still usually completes, while a paused/stuck animation can never hold boot
// longer than this.
export const PROGRESS_BAR_MAX_WAIT_MS = 1500;

// A setTimeout-shaped scheduler. Injected so the wait can be driven deterministically
// in tests and so the caller decides which timer to use (MyApp is the root component
// and deliberately uses the raw global setTimeout — it is never destroyed).
export type ScheduleTimeout = (callback: () => void, ms: number) => unknown;

// Resolve after `ms`, using the injected scheduler.
export function delayMs(ms: number, scheduleTimeout: ScheduleTimeout): Promise<void> {
  return new Promise<void>(resolve => {
    scheduleTimeout(() => resolve(), ms);
  });
}

// Wait for the progress-bar animation to finish, but never longer than `maxWaitMs`,
// and never reject.
//
// - A paused animation never settles, so the `maxWaitMs` delay wins and boot
//   continues.
// - A cancelled animation rejects its `finished` promise; we swallow that rejection
//   so it can never become an unhandled rejection.
// - A missing animation (null/undefined, e.g. the progress bar element was absent or
//   already at 100%) just waits out the delay.
export function waitForAnimationOrTimeout(
  animationFinished: Promise<unknown> | null | undefined,
  maxWaitMs: number,
  scheduleTimeout: ScheduleTimeout,
): Promise<void> {
  const timeout = delayMs(maxWaitMs, scheduleTimeout);
  if (!animationFinished) {
    return timeout;
  }
  // swallow a cancelled-animation rejection so it can never escape as an unhandled
  // rejection; map a normal resolve to undefined so the race result type is uniform.
  const settledAnimation = Promise.resolve(animationFinished).then(
    () => undefined,
    () => undefined,
  );
  return Promise.race([settledAnimation, timeout]);
}

// How long ONE step of the progress bar glides for.
//
// The bar is fed by replication batches, and there are far fewer of those than
// you would guess: a 1,404-document initial sync reported progress exactly 8
// times (196, 396, 596, 796, 994, 1134, 1213, 1215 documents). With a fixed
// 1000 ms glide per step that reads as "jump 14%, sit still, jump 14%" — which
// is what it looked like on device. Stretching each glide across the gap until
// the NEXT batch is expected keeps the bar in motion the whole time.
//
// The bar still never claims more than has actually been measured. Only the
// speed of getting to a measured value changes, never the value.
/** Glide length for the first step, before any gap has been observed. */
export const PROGRESS_STEP_DEFAULT_MS = 1000;
/** Floor, so a burst of quick batches cannot turn into a visible stutter. */
export const PROGRESS_STEP_MIN_MS = 400;
/** Ceiling, so one slow batch cannot leave the bar creeping imperceptibly. */
export const PROGRESS_STEP_MAX_MS = 8000;

/**
 * How long the next progress step should take, given how long ago the previous
 * one arrived.
 *
 * The gap just observed is the best available estimate of the gap about to
 * happen — replication batches arrive at a fairly steady rate — so animating
 * for that long means the bar is usually still moving when the next batch
 * lands. A missing measurement (the first step) falls back to the default.
 */
export function computeProgressStepDurationMs(msSinceLastUpdate: number | null | undefined): number {
  // if there is no previous step to learn from, use the plain default
  if (msSinceLastUpdate == null || !Number.isFinite(msSinceLastUpdate)) {
    return PROGRESS_STEP_DEFAULT_MS;
  }
  return Math.min(PROGRESS_STEP_MAX_MS, Math.max(PROGRESS_STEP_MIN_MS, msSinceLastUpdate));
}

/** One database's contribution to the boot progress bar. */
export interface SyncProgressContribution {
  /** true for the user's own database — the one whose download dominates boot */
  isUserDb?: boolean;
  /** total units of work this database has reported, 0 when it has reported none */
  syncTasksCount: number;
  /** units completed so far */
  syncTasksCompleted: number;
  /** false when this database has not published any sync-progress event yet */
  hasReported: boolean;
}

/**
 * Fraction (0..1) of the boot sync that is done, or null when nothing is
 * reporting work yet and the bar should be left alone.
 *
 * A database that has not reported yet contributes NOTHING — it must never abort
 * the whole calculation. That was the bug this helper was extracted to fix: the
 * caller used to bail out of the entire computation on the first database with no
 * progress entry, and `allDatabases` keeps growing as boot creates the shared
 * databases. Any one of them that never published (never synced, remote-only,
 * created late) froze the bar at 0 for the whole sync, so the only movement a user
 * ever saw was the final jump to 100% when boot forced the bar full.
 *
 * Returning null rather than 0 for "nothing to report" matters too: dividing by a
 * zero total yields NaN, and NaN poisons the caller's monotonic comparison.
 */
export function computeSyncProgressFraction(contributions: SyncProgressContribution[]): number | null {
  // Prefer the USER database on its own once it is reporting real work.
  //
  // Summing across databases looks fairer but is actively wrong here, because the
  // set of databases reporting CHANGES as boot proceeds. Measured on device: the
  // tiny `global_testcases` database reported 1 document, 1 done, at a moment when
  // it was the only one with a count — so the sum was 1/1 and the bar went
  // straight to 100%. The caller's monotonic gate then locked there, and every
  // later reading from the user database's real 16,577-document download (9%,
  // 18%, 28% …) was discarded as "not moving forward". The bar filled instantly
  // and then sat still for the entire sync.
  //
  // The user database is the one whose download takes the time, and it carries a
  // single stable denominator, so tracking it alone gives a bar that actually
  // rises with the data. Other databases are only used before it starts
  // reporting, and as the fallback when there is no user database.
  //
  // WHERE THE DENOMINATOR COMES FROM. The user database counts its own
  // documents for real — an ids-only allDocs read run alongside the replication
  // (DatabaseWrapper.countInitialSyncDocsAsync) — so once that answers, the
  // total here is a document count and the bar is proportional to the download.
  // Until it answers, and if it fails, the total falls back to PouchDB's
  // `pending`, which is a poor stand-in; computePullSyncTaskCounters in
  // initial-sync-progress.ts explains exactly why and carries the
  // measurement. So a reading taken very early in a sync can still be
  // pessimistic.
  const userDb = contributions.find(c => c.isUserDb);
  if (userDb) {
    // Hold the bar at nothing until it starts reporting, rather than letting the
    // small shared databases drive it upward first. The caller's monotonic gate
    // never moves backwards, so any height reached before the real download
    // begins is a height the real download can never climb out of.
    if (!userDb.hasReported || userDb.syncTasksCount === 0) return null;
    return userDb.syncTasksCompleted / userDb.syncTasksCount;
  }

  // no user database in play (e.g. a pre-login boot) — fall back to the aggregate
  let totalTasks = 0;
  let completedTasks = 0;
  for (const contribution of contributions) {
    if (!contribution.hasReported) continue;
    if (contribution.syncTasksCount === 0) continue;
    totalTasks += contribution.syncTasksCount;
    completedTasks += contribution.syncTasksCompleted;
  }
  if (totalTasks === 0) return null;
  return completedTasks / totalTasks;
}
